/**
 * BandMeter — the signature visual element of the FIX section.
 *
 * A slim 3px rail showing the SEO target zone as a filled teal segment
 * against a muted track, with a live marker at the current character count.
 * In range → marker + count in teal-dark. Out of range → coral.
 *
 * The rail encodes the SEO contract visually so the reviewer can SEE the
 * band without reading a scolding sentence. The number confirms it.
 */

import React from 'react';

import {
  META_TITLE_MIN,
  META_TITLE_MAX,
  META_DESCRIPTION_MIN,
  META_DESCRIPTION_MAX,
} from '@/lib/metadataCriteria';

export interface BandRange {
  min: number;
  max: number;
  /** Total scale of the rail (so the target zone isn't edge-bleed). */
  railMax: number;
}

// Thresholds come from the evaluator so the rail can never disagree with the
// rule that produced the violation.
export const BAND_RANGES: Record<string, BandRange> = {
  meta_title: { min: META_TITLE_MIN, max: META_TITLE_MAX, railMax: 80 },
  meta_description: {
    min: META_DESCRIPTION_MIN,
    max: META_DESCRIPTION_MAX,
    railMax: 200,
  },
};

function pct(value: number, railMax: number): number {
  return Math.max(0, Math.min(100, (value / railMax) * 100));
}

export interface BandMeterProps {
  current: number;
  range: BandRange;
}

export const BandMeter: React.FC<BandMeterProps> = ({ current, range }) => {
  const { min, max, railMax } = range;
  const inRange = current >= min && current <= max;

  const zoneStart = pct(min, railMax);
  const zoneWidth = pct(max, railMax) - zoneStart;
  const markerPos = pct(Math.max(0, current), railMax);

  const markerColor = inRange ? 'bg-teal-dark' : 'bg-coral';
  const countColor = inRange ? 'text-teal-dark' : 'text-coral-dark';
  const zoneColor = inRange ? 'bg-teal' : 'bg-teal/40';

  return (
    <div className="flex items-center gap-2">
      <div className="relative h-[3px] flex-1 rounded-full bg-muted">
        {/* Target zone segment */}
        <div
          className={`absolute top-0 h-full rounded-full motion-safe:transition-colors ${zoneColor}`}
          style={{ left: `${zoneStart}%`, width: `${zoneWidth}%` }}
          aria-hidden="true"
        />
        {/* Live marker */}
        <div
          className={`absolute top-1/2 h-2.5 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full motion-safe:transition-colors ${markerColor}`}
          style={{ left: `${markerPos}%` }}
          aria-hidden="true"
        />
      </div>
      <span
        className={`shrink-0 text-xs tabular-nums motion-safe:transition-colors ${countColor}`}
        aria-label={`${current} characters, target ${min} to ${max}`}
      >
        {current} / {min}–{max}
      </span>
    </div>
  );
};

export default BandMeter;
